import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, X, Star, ChevronDown, ChevronRight, ChevronLeft, Check, Dumbbell, Target, Heart, Zap, Users, Clock, Award, ArrowRight, Play, Sparkles, Shield, TrendingUp, Activity, CheckCircle2, Calendar, ExternalLink, HelpCircle, MessageCircle, Home, Flame, Mountain, Scan, Apple, CircleDot, ParkingCircle, Globe, Monitor, Video, Instagram, Facebook, Linkedin, Gift, LogIn, Trophy, BarChart3, Link2, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL, STUDIO_IMAGE, LAURENT_IMAGE, TARAH_IMAGE, BOOKING_URL, EMS_SITE_URL, WHATSAPP_URL, SOCIAL_LINKS, IMAGES } from '../data/constants';
import { services } from '../data/services';
import { blogArticles } from '../data/blogData';
import { pricingCategories } from '../data/pricingData';
import { testimonials } from '../data/testimonials';
import { faqItems } from '../data/faqData';
import { AnimatedSection, BackNav, useIntersectionObserver, SectionBadge, AnimatedBadge, ParticlesBackground } from '../components/shared';
import { ServiceCard, ServiceCardWhite } from '../components/ServiceCards';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { HomeContactSection } from '../components/HomeContactSection';
import { BlogArticleModal } from '../components/BlogArticleModal';

const FAQPage = () => {
  const { language } = useLanguage();
  
  return (
    <main className="pt-32" data-testid="faq-page">
      <SEOHead
        title="FAQ | Questions Fréquentes Personal Training Nyon | Body-Up"
        description="Réponses à vos questions : Personal Training Nyon, séances EMS, tarifs, horaires, parking gratuit, premier rendez-vous GRATUIT. Studio privé 150m² à Nyon."
        path="/faq"
      />
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black min-h-screen relative overflow-hidden">
        {/* Background Glow Effects */}
        <div className="absolute top-20 left-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-20 right-0 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl opacity-30" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <BackNav />
          <AnimatedSection>
            <div className="text-center mb-16">
              <SectionBadge emoji="❓">FAQ</SectionBadge>
              <h1 className="text-4xl md:text-6xl font-bold text-white mt-4">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-400">Questions Fréquentes</span>
              </h1>
              <p className="text-gray-400 mt-4 max-w-2xl mx-auto text-lg">
                {language === 'fr' 
                  ? "Tout ce que vous devez savoir sur le Personal Training chez Body-Up 🎯"
                  : "Everything you need to know about Personal Training at Body-Up 🎯"
                }
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="grid gap-4">
              {faqItems.map((item, index) => (
                <div key={index} className="group">
                  <Accordion type="single" collapsible>
                    <AccordionItem value={`item-${index}`} className="border-0">
                      <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/20 hover:border-primary/50 transition-all duration-500 hover:shadow-lg hover:shadow-primary/10 overflow-hidden">
                        <AccordionTrigger className="text-white text-left hover:text-primary px-4 sm:px-6 py-4 sm:py-5 hover:no-underline w-full">
                          <span className="flex items-center gap-3 sm:gap-4 w-full">
                            <span className="flex-shrink-0 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary to-pink-500 rounded-xl sm:rounded-2xl text-xl sm:text-2xl shadow-lg shadow-primary/30 group-hover:scale-110 transition-transform">
                              {item.emoji}
                            </span>
                            <span className="font-semibold text-base sm:text-lg flex-1 text-left">{language === 'fr' ? item.question : item.questionEn}</span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                          <div className="bg-gradient-to-br from-primary/15 to-primary/5 rounded-xl p-4 border border-primary/20">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {(language === 'fr' ? item.answerPoints : item.answerPointsEn).map((point, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm sm:text-base animate-fadeInUp" style={{ animationDelay: `${i * 100}ms` }}>
                                  <span className={`font-bold ${i % 2 === 0 ? 'text-primary' : 'text-white'}`}>{point}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </AccordionContent>
                      </div>
                    </AccordionItem>
                  </Accordion>
                </div>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <div className="mt-16 text-center">
              <div className="bg-gradient-to-br from-primary/20 to-pink-500/10 rounded-2xl p-8 border border-primary/30">
                <h3 className="text-2xl font-bold text-white mb-4">
                  💬 {language === 'fr' ? "Une autre question ?" : "Another question?"}
                </h3>
                <p className="text-gray-300 mb-6">
                  {language === 'fr' 
                    ? "Notre équipe est là pour vous répondre. Contactez-nous pour toute information supplémentaire."
                    : "Our team is here to help. Contact us for any additional information."
                  }
                </p>
                <Link to="/contact">
                  <Button className="bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-500/90 text-white shadow-lg shadow-primary/30 hover:scale-105 transition-all">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {language === 'fr' ? "Nous contacter" : "Contact us"}
                  </Button>
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
};

// Tarifs Page - WAHOU Style like trainiq-ems.ch

export default FAQPage;
